import React, { useCallback, useMemo } from "react";
import PerformanceTablePanel from "./PerformanceTablePanel";
import {
  type TableDataRow,
  type KpLeadingDimension,
  getKpPerformanceTableColumns,
  mapKpTableRows,
} from "./performanceChartModel";

export interface KpPerformanceTableProps {
  isDark: boolean;
  cardBg: string;
  textSub: string;
  chartBusy: boolean;
  /** Raw KP tableData rows (metric blocks); flattened internally. */
  rows: any[];
  /** Plan year to highlight. */
  selectedYear: number;
  /** Leading column dimension, driven by the Performance Chart date toggle. */
  leadingDimension?: string;
  /** Active value-metric ids; columns whose metric is not active are grayed out. */
  activeValueFilters?: Set<string>;
  /** Whether projected columns are enabled (otherwise grayed out). */
  showProjections?: boolean;
}

/**
 * Key Person performance table. KP returns each metric as an { actual, projected }
 * block, so this component flattens the rows, builds flat metric/projected columns, and
 * renders them through the shared PerformanceTablePanel (active/disabled graying
 * handled there via activeValueFilters + showProjections).
 *
 * The single leading column (Year / Plan Year / Insured Age) follows the date
 * toggle in the Performance Chart section.
 */
const KpPerformanceTable: React.FC<KpPerformanceTableProps> = ({
  isDark,
  cardBg,
  textSub,
  chartBusy,
  rows,
  selectedYear,
  leadingDimension = "planYear",
  activeValueFilters,
  showProjections,
}) => {
  const columns = useMemo(
    () => getKpPerformanceTableColumns(leadingDimension as KpLeadingDimension),
    [leadingDimension],
  );
  const tableRows = useMemo(() => mapKpTableRows(rows), [rows]);

  const rowClassName = useCallback(
    (rowData: TableDataRow) => (rowData?.planYear === selectedYear ? "font-semibold" : ""),
    [selectedYear],
  );

  return (
    <PerformanceTablePanel
      isDark={isDark}
      cardBg={cardBg}
      textSub={textSub}
      chartBusy={chartBusy}
      columns={columns}
      rows={tableRows as unknown as TableDataRow[]}
      rowClassName={rowClassName}
      activeValueFilters={activeValueFilters}
      showProjections={showProjections}
      groups={[]}
    />
  );
};

export default KpPerformanceTable;
